import { useEffect, useRef } from "react";

import { FileUploadAndParse } from "@fronterahealth/frontera-ui-components";

import { FileTypeEnum, useStartLearnerMetadataCreationFromBhtiaFileMutation } from "@api/graphql/types-and-hooks";
import {
  useGetIntakeFileParseStatusRetriever,
  useGetIntakeFileUploadCompleteCallback,
  useGetIntakeRemoveFileCallback,
  useGetIntakeS3BucketUploadCallback,
  useIntakeS3BucketUrlRetriever,
} from "@components/CreateNewClientDialog/hooks";

interface UploadBhtiaFileProps {
  setLearnerId: (learnerId: string | undefined) => void;
  disabled: boolean;
  fileUploadedCallback: () => void;
  setLearnerFileId: (learnerFileId: string | undefined) => void;
  onFileRemoved?: () => void;
  onFileUploadStarted?: () => void;
  onUploadError?: () => void;
}

export const UploadBhtiaFile: React.FC<UploadBhtiaFileProps> = ({
  setLearnerId,
  disabled,
  fileUploadedCallback,
  setLearnerFileId,
  onFileRemoved,
  onFileUploadStarted,
  onUploadError,
}) => {
  const mountedRef = useRef(true);
  useEffect(() => {
    // React StrictMode double-invokes effects in dev (mount -> cleanup ->
    // remount) to surface exactly this kind of bug: without resetting the
    // ref here, the simulated cleanup sets it false and nothing ever sets it
    // back to true on the real mount, permanently blocking every guarded
    // callback below for the rest of the component's real lifetime.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const guardedSetLearnerId = (id: string | undefined) => {
    if (mountedRef.current) setLearnerId(id);
  };
  const guardedSetLearnerFileId = (id: string | undefined) => {
    if (mountedRef.current) setLearnerFileId(id);
  };
  const guardedFileUploadedCallback = () => {
    if (mountedRef.current) fileUploadedCallback();
  };
  const guardedOnFileUploadStarted = () => {
    if (mountedRef.current) onFileUploadStarted?.();
  };
  const guardedOnFileRemoved = () => {
    if (mountedRef.current) onFileRemoved?.();
  };
  const guardedOnUploadError = () => {
    if (mountedRef.current) onUploadError?.();
  };

  const baseS3BucketUrlRetriever = useIntakeS3BucketUrlRetriever(guardedSetLearnerId);
  const S3BucketUrlRetriever: typeof baseS3BucketUrlRetriever = async (params) => {
    guardedOnFileUploadStarted();
    try {
      return await baseS3BucketUrlRetriever(params);
    } catch (e) {
      guardedOnUploadError();
      throw e;
    }
  };
  const S3BucketUploadCallback = useGetIntakeS3BucketUploadCallback();
  const metadataCreationMutation = useStartLearnerMetadataCreationFromBhtiaFileMutation();
  const reportFileUploadCompleteCallback = useGetIntakeFileUploadCompleteCallback(
    metadataCreationMutation,
    "startLearnerMetadataCreationFromBhtia",
  );
  const fileParseStatusRetriever = useGetIntakeFileParseStatusRetriever(
    guardedFileUploadedCallback,
    guardedSetLearnerFileId,
    guardedOnUploadError,
  );
  const baseRemoveFileCallback = useGetIntakeRemoveFileCallback(guardedSetLearnerFileId);
  const removeFileCallback = async (fileId: string) => {
    await baseRemoveFileCallback(fileId);
    guardedOnFileRemoved();
  };
  return (
    <FileUploadAndParse
      key="bhtia"
      fileKind={FileTypeEnum.Bhtia}
      title=""
      supportedFileTypes={["pdf"]}
      maxSize={"20MB"}
      singleFileUpload
      S3BucketUrlRetriever={S3BucketUrlRetriever}
      S3BucketUploadCallback={S3BucketUploadCallback}
      reportFileUploadCompleteCallback={reportFileUploadCompleteCallback}
      fileParseStatusRetriever={fileParseStatusRetriever}
      removeFileCallback={removeFileCallback}
      disabled={disabled}
    />
  );
};
